import { startTransition } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

// Number-list order: idle scheduling, preload recovery, commit acknowledgment (milliseconds).
// Recovery limits are not delays on the normal path.
const DEFAULT_TIMEOUTS_MS: [number, number, number] = [1_000, 5_000, 5_000];
// How long a light table in the view stays hidden while it upgrades, before showing its light rows.
const HIDE_UNTIL_UPGRADED_MS = 1_000;
// Tables whose light rows have shown, which a later scheduler for the same DOM must not hide again.
const lightRowsShown = new WeakSet<Element>();

type WaitPhase = 'idle' | 'preload' | 'commit';

export type LightSubtreeWait = {
	outcome: 'completed' | 'timedOut' | 'failed' | 'cancelled' | 'fallback';
	durationMs: number;
} & (
	| { phase: WaitPhase; timeoutMs: number }
	| {
			phase: 'visibleLight';
			activationBufferPx: number;
			viewportObserved: boolean;
			shownInLightMode: boolean;
			visibleAtCommit: boolean;
			viewportToCommitMs?: number;
			wasHidden: boolean;
			interactionProtected: boolean;
	  }
);

type ViewportWait = {
	visible: Set<Element>;
	enteredAt?: number;
	durationMs: number;
	viewportObserved: boolean;
	shownInLightMode: boolean;
	wasHidden: boolean;
	interactionProtected: boolean;
};

type FinishWait = (outcome: LightSubtreeWait['outcome']) => void;

type Interaction = { activeElement: Element | null; selection: Selection | null };

type Subtree = {
	// The serializer creates one LightSubtree boundary per top-level ADF subtree.
	activate?: () => void;
	elements: Set<HTMLElement>;
	visible: Set<Element>;
	// In the view itself, not just near it, so an unfinished upgrade must land now.
	inView: Set<Element>;
	active: boolean;
	// Merged rows look unlike the full table, so a light table shows blank space while it upgrades.
	hideUntilUpgraded?: boolean;
	// An idle upgrade renders in a transition and can outlast the commit timeout, which releases
	// `inFlight`; these say whether an on-demand upgrade still has to finish it.
	committed?: boolean;
	activatedNow?: boolean;
	prepare?: () => Promise<void>;
	preparation: 'pending' | 'loading' | 'ready';
	preparationTimer?: number;
	finishPreparationWait?: FinishWait;
	viewportWait?: ViewportWait;
	// 'ready' also follows a failed or timed-out preload, so normal upgrades can recover.
	preloaded: boolean;
};

/**
 * Coordinates light-to-full upgrades for one serialized document snapshot.
 *
 * An IntersectionObserver queues visible subtrees and drops those that leave view. Each subtree
 * preloads its full components while keeping light content displayed. During idle time, the
 * scheduler activates one ready subtree without disturbing focus or selection, waits for its React
 * boundary to acknowledge the commit, then yields before the next upgrade. Activated subtrees stay
 * full. An idle deadline prevents starvation, failed or timed-out preloads fall back to normal
 * full-component loading, and a missing commit acknowledgment eventually releases the queue.
 *
 * With table virtualization, subtrees queue a viewport ahead of the view and upgrade in a
 * transition; a second observer finishes an upgrade synchronously once its subtree is in the view,
 * including one whose transition stalled, and light tables stay hidden until they upgrade.
 * Stopping cancels observers, listeners, and scheduled work while preserving state for a restart.
 */
export class LightSubtreeScheduler {
	constructor(private readonly options: { allowVirtualizeTables?: boolean } = {}) {}

	private subtrees = new Map<number, Subtree>();
	private elementSubtrees = new Map<Element, Subtree>();
	private queued = new Set<Subtree>();
	private observer?: IntersectionObserver;
	private viewportObserver?: IntersectionObserver;
	private cancelScheduled?: () => void;
	private inFlight?: Subtree;
	private commitTimer?: number;
	private running = false;
	private readonly timeouts = readTimeouts();
	private activationBufferPx = 0;
	private reportWait?: (wait: LightSubtreeWait) => void;
	private finishCommitWait?: FinishWait;
	private hidden = new Set<HTMLElement>();
	private revealTimers = new Map<Element, number>();
	private viewObserver?: IntersectionObserver;

	private schedule = (): void => {
		// Repeated visibility/ref events share one callback. Check eligibility only when it runs.
		if (!this.running || this.inFlight || this.cancelScheduled || !this.queued.size) {
			return;
		}
		this.cancelScheduled = scheduleWhenIdle(this.runNext, this.timeouts.idle, () =>
			this.measureWait('idle'),
		);
	};

	private runNext = (): void => {
		this.cancelScheduled = undefined;
		if (!this.running || this.inFlight) {
			return;
		}
		const subtree = this.nextEligibleSubtree();
		if (!subtree) {
			return;
		}
		if (subtree.preparation === 'pending') {
			this.prepareSubtree(subtree);
			this.schedule(); // Other subtrees can prepare while this one loads.
			return;
		}

		this.inFlight = subtree;
		this.finishCommitWait = this.measureWait('commit');
		this.startCommitTimeout();
		subtree.active = true;
		this.queued.delete(subtree);
		subtree.elements.forEach((element) => this.observer?.unobserve(element));
		if (this.options.allowVirtualizeTables) {
			// React renders the full subtree in slices and yields to input.
			startTransition(() => subtree.activate?.());
		} else {
			subtree.activate?.();
		}
	};

	private getOrCreateSubtree(id: number): Subtree {
		let subtree = this.subtrees.get(id);
		if (!subtree) {
			subtree = {
				elements: new Set(),
				visible: new Set(),
				inView: new Set(),
				active: false,
				preparation: 'ready',
				preloaded: true,
			};
			this.subtrees.set(id, subtree);
		}
		return subtree;
	}

	observe = (id: number, element: HTMLElement, hideUntilUpgraded = false): (() => void) => {
		const subtree = this.getOrCreateSubtree(id);
		subtree.hideUntilUpgraded ||= hideUntilUpgraded;
		subtree.elements.add(element);
		if (subtree.hideUntilUpgraded) {
			this.hide(element);
		}
		this.elementSubtrees.set(element, subtree);
		if (!subtree.active) {
			this.observeElement(element);
			this.observeViewport(subtree, element);
		}
		return () => {
			// React detaches light refs before the full boundary acknowledges its commit.
			this.flushViewportEntries();
			this.viewportObserver?.unobserve(element);
			if (subtree.viewportWait && !subtree.active) {
				this.leaveViewport(subtree, element, performance.now());
			}
			this.observer?.unobserve(element);
			this.viewObserver?.unobserve(element);
			subtree.inView.delete(element);
			this.reveal(element);
			this.elementSubtrees.delete(element);
			subtree.elements.delete(element);
			subtree.visible.delete(element);
			if (!subtree.visible.size) {
				this.queued.delete(subtree);
			}
		};
	};

	mount(id: number, activate: () => void, prepare?: () => Promise<void>): () => void {
		const subtree = this.getOrCreateSubtree(id);
		subtree.activate = activate;
		// Preserve readiness when React re-registers the effect in this serializer snapshot.
		if (prepare && !subtree.prepare) {
			subtree.prepare = prepare;
			subtree.preparation = 'pending';
			subtree.preloaded = false;
		}
		this.startPreparationTimeout(subtree);
		if (subtree.active) {
			activate();
		} else if (subtree.visible.size) {
			// An effect can re-register while its DOM refs and intersection state stay unchanged.
			this.queued.add(subtree);
		}
		// Its element can already be in view, before it could upgrade.
		this.upgradeInView(subtree);
		this.schedule();
		return () => {
			subtree.activate = undefined;
			this.queued.delete(subtree);
			this.clearPreparationTimer(subtree);
			subtree.finishPreparationWait?.('cancelled');
			subtree.finishPreparationWait = undefined;
			if (this.inFlight === subtree) this.finishCommit('cancelled');
			else this.finishViewportWait(subtree, 'cancelled');
		};
	}

	/** Start preloading ahead of an upgrade that is about to be forced. */
	prepareNow(id: number): void {
		const subtree = this.subtrees.get(id);
		if (subtree?.preparation === 'pending') {
			this.prepareSubtree(subtree);
		}
	}

	/** Whether the subtree's full components loaded, so an upgrade renders no loading states. */
	isPreloaded(id: number): boolean {
		return this.subtrees.get(id)?.preloaded ?? false;
	}

	/** Whether the selection wholly contains the subtree's DOM, so replacing it moves no boundary. */
	isWhollySelected(id: number, selection: Selection): boolean {
		const elements = this.subtrees.get(id)?.elements;
		return Boolean(
			elements?.size && [...elements].every((element) => selection.containsNode(element, false)),
		);
	}

	/**
	 * Activate now, skipping preparation, idle time, and interaction protection. An upgrade still
	 * rendering in a transition is activated again, synchronously.
	 */
	activateNow(id: number): void {
		const subtree = this.subtrees.get(id);
		if (subtree) {
			this.activateSubtreeNow(subtree);
		}
	}

	private activateSubtreeNow(subtree: Subtree): void {
		if (!subtree.activate || (subtree.active && (subtree.committed || subtree.activatedNow))) {
			return;
		}
		subtree.active = true;
		subtree.activatedNow = true;
		this.queued.delete(subtree);
		subtree.elements.forEach((element) => {
			this.observer?.unobserve(element);
			this.viewObserver?.unobserve(element);
		});
		subtree.activate();
	}

	// Near the view an upgrade waits for idle time and renders in a transition; once the subtree is
	// in the view, an upgrade that has not landed finishes synchronously, after its preload.
	private upgradeInView(subtree: Subtree): void {
		if (!subtree.activate || subtree.committed || subtree.activatedNow || !subtree.inView.size) {
			return;
		}
		if (subtree.preparation === 'pending') {
			this.prepareSubtree(subtree);
			return;
		}
		if (subtree.preparation === 'loading') {
			return;
		}
		const interaction = readInteraction();
		if (!interaction || this.hasProtectedInteraction(subtree, interaction)) {
			return;
		}
		this.activateSubtreeNow(subtree);
	}

	acknowledge(id: number): void {
		const subtree = this.subtrees.get(id);
		if (!subtree) {
			return;
		}
		if (subtree.activate) {
			subtree.active = true;
			subtree.committed = true;
			this.queued.delete(subtree);
			subtree.elements.forEach((element) => this.viewObserver?.unobserve(element));
		}
		// A late acknowledgment must not release another subtree's upgrade.
		if (this.inFlight === subtree) {
			this.finishCommit();
		}
	}

	// Created for the first light element, whose scrolling container clips every other one too.
	private observeElement(element: Element): void {
		if (!this.running) {
			return;
		}
		if (!this.observer) {
			if (this.options.allowVirtualizeTables) {
				const root = findScrollContainer(element);
				// Upgrade a viewport ahead, so the size change happens out of view. A margin only grows
				// the root, so the root must be the container that scrolls, or that container would clip it.
				this.observer = this.createNearObserver({ root, rootMargin: '100% 0px' });
				this.viewObserver = this.createViewObserver(root);
			} else {
				this.observer = this.createNearObserver({ rootMargin: `${this.activationBufferPx}px 0px` });
			}
		}
		this.observer.observe(element);
		this.viewObserver?.observe(element);
	}

	private createNearObserver(options?: IntersectionObserverInit): IntersectionObserver {
		const observer: IntersectionObserver = new IntersectionObserver((entries) => {
			// Ignore deliveries from a previous effect lifecycle after a restart.
			if (!this.running || this.observer !== observer) {
				return;
			}
			for (const entry of entries) {
				const subtree = this.elementSubtrees.get(entry.target);
				if (!subtree || subtree.active) {
					continue;
				}
				if (entry.isIntersecting) {
					subtree.visible.add(entry.target);
					this.queued.add(subtree);
				} else {
					subtree.visible.delete(entry.target);
					if (!subtree.visible.size) {
						this.queued.delete(subtree);
					}
				}
			}
			this.schedule();
		}, options);
		return observer;
	}

	private createViewObserver(root: Element | null): IntersectionObserver {
		const observer: IntersectionObserver = new IntersectionObserver(
			(entries) => {
				if (!this.running || this.viewObserver !== observer) {
					return;
				}
				for (const entry of entries) {
					const subtree = this.elementSubtrees.get(entry.target);
					if (!subtree) {
						continue;
					}
					if (entry.isIntersecting) {
						subtree.inView.add(entry.target);
						this.startReveal(entry.target);
						this.upgradeInView(subtree);
					} else {
						subtree.inView.delete(entry.target);
						this.cancelReveal(entry.target);
					}
				}
			},
			{ root, rootMargin: '0px' },
		);
		return observer;
	}

	// Hidden from mount, so a fast scroll never paints merged rows before the observers run.
	private hide(element: HTMLElement): void {
		if (!this.options.allowVirtualizeTables || lightRowsShown.has(element)) {
			return;
		}
		element.style.opacity = '0';
		this.hidden.add(element);
	}

	// A stalled or blocked upgrade still shows the light rows once the table has been in view a while.
	private startReveal(element: Element): void {
		if (!this.hidden.has(element as HTMLElement) || this.revealTimers.has(element)) {
			return;
		}
		this.revealTimers.set(
			element,
			window.setTimeout(() => {
				lightRowsShown.add(element);
				this.reveal(element);
			}, HIDE_UNTIL_UPGRADED_MS),
		);
	}

	private cancelReveal(element: Element): void {
		window.clearTimeout(this.revealTimers.get(element));
		this.revealTimers.delete(element);
	}

	private reveal(element: Element): void {
		this.cancelReveal(element);
		if (this.hidden.delete(element as HTMLElement)) {
			(element as HTMLElement).style.opacity = '';
		}
	}

	private hasProtectedInteraction(
		subtree: Subtree,
		{ activeElement, selection }: Interaction,
	): boolean {
		for (const element of subtree.elements) {
			if (
				element.contains(activeElement) ||
				(selection && !selection.isCollapsed && selection.containsNode(element, true))
			) {
				if (subtree.viewportWait) subtree.viewportWait.interactionProtected = true;
				return true;
			}
		}
		return false;
	}

	private nextEligibleSubtree(): Subtree | undefined {
		const interaction = readInteraction();
		if (!interaction) {
			return undefined;
		}
		for (const subtree of this.queued) {
			if (
				!subtree.active &&
				subtree.preparation !== 'loading' &&
				subtree.activate &&
				subtree.visible.size > 0 &&
				!this.hasProtectedInteraction(subtree, interaction)
			) {
				return subtree;
			}
		}
		return undefined;
	}

	private reportFailure(message: string): void {
		getMonitoringClient().submitError(new Error(message), {
			attribution: Attribution.BACKBONE,
			partialFailure: true,
		});
	}

	private clearPreparationTimer(subtree: Subtree): void {
		window.clearTimeout(subtree.preparationTimer);
		subtree.preparationTimer = undefined;
	}

	private finishPreparation(
		subtree: Subtree,
		outcome: LightSubtreeWait['outcome'] = 'completed',
		failure?: string,
	): void {
		// Ignore late resolution/rejection after a timeout; never activate or report twice.
		if (subtree.preparation !== 'loading') {
			return;
		}
		this.clearPreparationTimer(subtree);
		subtree.preparation = 'ready';
		subtree.preloaded = failure === undefined;
		// Preloading is optional. Recheck visibility and interaction protection in the queue.
		this.schedule();
		this.upgradeInView(subtree);
		subtree.finishPreparationWait?.(outcome);
		subtree.finishPreparationWait = undefined;
		if (failure) {
			this.reportFailure(failure);
		}
	}

	private startPreparationTimeout(subtree: Subtree): void {
		if (
			this.running &&
			subtree.activate &&
			subtree.preparation === 'loading' &&
			subtree.preparationTimer === undefined
		) {
			subtree.preparationTimer = window.setTimeout(() => {
				this.finishPreparation(
					subtree,
					'timedOut',
					'Light rendering: subtree preload timed out; falling back to full component loading',
				);
			}, this.timeouts.preload);
		}
	}

	private prepareSubtree(subtree: Subtree): void {
		subtree.preparation = 'loading';
		subtree.finishPreparationWait = this.measureWait('preload');
		this.startPreparationTimeout(subtree);
		void Promise.resolve()
			.then(subtree.prepare)
			.then(
				() => this.finishPreparation(subtree),
				() =>
					this.finishPreparation(
						subtree,
						'failed',
						'Light rendering: subtree preload failed; falling back to full component loading',
					),
			);
	}

	private startCommitTimeout(): void {
		if (!this.inFlight || this.commitTimer !== undefined) {
			return;
		}
		this.commitTimer = window.setTimeout(() => {
			const stalled = this.inFlight;
			this.finishCommit('timedOut');
			this.reportFailure('Light rendering: subtree commit timed out; continuing visible upgrades');
			// The transition may still commit, but the subtree stays light until it does, and it can
			// stay pending while the full subtree suspends or keeps being interrupted. Finish it now if
			// it is in view and nothing inside is focused or selected; otherwise the view observer will.
			if (stalled) {
				this.upgradeInView(stalled);
			}
		}, this.timeouts.commit);
	}

	private finishCommit(outcome: LightSubtreeWait['outcome'] = 'completed'): void {
		window.clearTimeout(this.commitTimer);
		this.commitTimer = undefined;
		if (this.inFlight) this.finishViewportWait(this.inFlight, outcome);
		this.inFlight = undefined;
		this.schedule();
		this.finishCommitWait?.(outcome);
		this.finishCommitWait = undefined;
	}

	private observeViewport(subtree: Subtree, element: Element): void {
		if (!this.viewportObserver) return;
		subtree.viewportWait ??= {
			visible: new Set(),
			durationMs: 0,
			viewportObserved: false,
			shownInLightMode: false,
			wasHidden: isDocumentHidden(),
			interactionProtected: false,
		};
		this.viewportObserver.observe(element);
	}

	private leaveViewport(subtree: Subtree, element: Element, time: number): void {
		const wait = subtree.viewportWait;
		if (!wait) return;
		wait.visible.delete(element);
		if (!wait.visible.size && wait.enteredAt !== undefined) {
			wait.durationMs += Math.max(0, time - wait.enteredAt);
			wait.enteredAt = undefined;
		}
	}

	private recordViewportEntries = (entries: IntersectionObserverEntry[]): void => {
		for (const entry of entries) {
			const subtree = this.elementSubtrees.get(entry.target);
			const wait = subtree?.viewportWait;
			if (!subtree || !wait) continue;
			wait.viewportObserved = true;
			wait.wasHidden ||= isDocumentHidden();
			// Edge contact at threshold zero does not mean pixels were visible.
			if (entry.isIntersecting && entry.intersectionRatio > 0) {
				if (!wait.visible.size) wait.enteredAt = entry.time;
				wait.visible.add(entry.target);
				wait.shownInLightMode = true;
			} else {
				this.leaveViewport(subtree, entry.target, entry.time);
			}
		}
	};

	private flushViewportEntries(): void {
		if (this.viewportObserver) this.recordViewportEntries(this.viewportObserver.takeRecords());
	}

	private finishViewportWait(subtree: Subtree, outcome: LightSubtreeWait['outcome']): void {
		if (!subtree.viewportWait) return;
		this.flushViewportEntries();
		const wait = subtree.viewportWait;
		const visibleMs =
			wait.enteredAt === undefined ? 0 : Math.max(0, performance.now() - wait.enteredAt);
		this.reportWait?.({
			phase: 'visibleLight',
			outcome,
			durationMs: wait.durationMs + visibleMs,
			activationBufferPx: this.activationBufferPx,
			viewportObserved: wait.viewportObserved,
			shownInLightMode: wait.shownInLightMode,
			visibleAtCommit: outcome === 'completed' && wait.enteredAt !== undefined,
			viewportToCommitMs:
				outcome === 'completed' && wait.enteredAt !== undefined ? visibleMs : undefined,
			wasHidden: wait.wasHidden || isDocumentHidden(),
			interactionProtected: wait.interactionProtected,
		});
		subtree.elements.forEach((element) => this.viewportObserver?.unobserve(element));
		subtree.viewportWait = undefined;
	}

	private onVisibilityChange = (): void => {
		if (isDocumentHidden()) {
			this.subtrees.forEach((subtree) => {
				if (subtree.viewportWait) subtree.viewportWait.wasHidden = true;
			});
		}
	};

	private measureWait(phase: WaitPhase): FinishWait | undefined {
		const reportWait = this.reportWait;
		if (!reportWait) return;
		const startedAt = performance.now();
		let finished = false;
		return (outcome) => {
			if (finished) return;
			finished = true;
			reportWait({
				phase,
				outcome,
				durationMs: performance.now() - startedAt,
				timeoutMs: this.timeouts[phase],
			});
		};
	}

	// Focus or a selection leaving a subtree lifts its protection: queue it, or finish it if in view.
	private onInteractionChange = (): void => {
		this.schedule();
		this.subtrees.forEach((subtree) => this.upgradeInView(subtree));
	};

	private onFocusOut = (): void => {
		void Promise.resolve().then(this.onInteractionChange);
	};

	start(reportWait?: (wait: LightSubtreeWait) => void): () => void {
		const documentRef = typeof document === 'undefined' ? undefined : document;
		const windowRef = typeof window === 'undefined' ? undefined : window;
		if (!documentRef || !windowRef) {
			return () => {};
		}
		this.running = true;
		// The metrics experiment controls collection; report every enabled document.
		this.reportWait = reportWait;
		this.activationBufferPx = readActivationBuffer();
		if (this.reportWait) {
			const viewportObserver = new IntersectionObserver(
				(entries) => {
					if (this.running && this.viewportObserver === viewportObserver) {
						this.recordViewportEntries(entries);
					}
				},
				{ rootMargin: '0px', threshold: [0, 0.0001] },
			);
			this.viewportObserver = viewportObserver;
			documentRef.addEventListener('visibilitychange', this.onVisibilityChange);
		}
		this.subtrees.forEach((subtree) => this.startPreparationTimeout(subtree));
		this.startCommitTimeout();
		this.elementSubtrees.forEach((subtree, element) => {
			if (!subtree.active) {
				// Stopping showed every hidden table; a restart hides them again.
				if (subtree.hideUntilUpgraded) {
					this.hide(element as HTMLElement);
				}
				this.observeElement(element);
				this.observeViewport(subtree, element);
			}
		});
		documentRef.addEventListener('focusout', this.onFocusOut);
		documentRef.addEventListener('selectionchange', this.onInteractionChange);
		this.schedule();
		return () => {
			this.running = false;
			this.observer?.disconnect();
			this.observer = undefined;
			this.viewObserver?.disconnect();
			this.viewObserver = undefined;
			this.hidden.forEach((element) => this.reveal(element));
			this.queued.clear();
			this.subtrees.forEach((subtree) => {
				subtree.visible.clear();
				subtree.inView.clear();
				this.clearPreparationTimer(subtree);
				this.finishViewportWait(subtree, 'cancelled');
				subtree.finishPreparationWait?.('cancelled');
				subtree.finishPreparationWait = undefined;
			});
			// Resumed preloads/commits must not produce artificially short successful samples.
			this.finishCommitWait?.('cancelled');
			this.finishCommitWait = undefined;
			window.clearTimeout(this.commitTimer);
			this.commitTimer = undefined;
			this.cancelScheduled?.();
			this.cancelScheduled = undefined;
			this.viewportObserver?.disconnect();
			this.viewportObserver = undefined;
			this.reportWait = undefined;
			documentRef.removeEventListener('visibilitychange', this.onVisibilityChange);
			documentRef.removeEventListener('focusout', this.onFocusOut);
			documentRef.removeEventListener('selectionchange', this.onInteractionChange);
		};
	}
}

function isDocumentHidden(): boolean {
	return typeof document !== 'undefined' && document.hidden;
}

function readActivationBuffer(): number {
	const value = StatsigConfigurations.getNumber('cc_light_mode_activation_buffer_px', 0);
	return Number.isFinite(value) && value >= 0 && value <= 2000 ? value : 0;
}

// The reader's focus and selection, which an upgrade must not replace.
function readInteraction(): Interaction | undefined {
	const documentRef = typeof document === 'undefined' ? undefined : document;
	const windowRef = typeof window === 'undefined' ? undefined : window;
	if (!documentRef || !windowRef) {
		return undefined;
	}
	return { activeElement: documentRef.activeElement, selection: windowRef.getSelection() };
}

// Read the three config entries once per scheduler.
function readTimeouts() {
	const [
		idle = DEFAULT_TIMEOUTS_MS[0],
		preload = DEFAULT_TIMEOUTS_MS[1],
		commit = DEFAULT_TIMEOUTS_MS[2],
	] = StatsigConfigurations.getNumberList(
		'cc_light_mode_rendering_timeouts_ms',
		DEFAULT_TIMEOUTS_MS,
	);
	return { idle, preload, commit };
}

// Always yield to a fresh task between commits, then prefer idle time with a deadline.
// Cancellation also guards callbacks delivered after a scheduler stop/restart.
function scheduleWhenIdle(
	callback: () => void,
	timeout: number,
	measureWait: () => FinishWait | undefined,
): () => void {
	let cancelled = false;
	let idleHandle: number | undefined;
	let finishWait: FinishWait | undefined;
	const run = (deadline?: IdleDeadline) => {
		if (cancelled) return;
		finishWait?.(deadline ? (deadline.didTimeout ? 'timedOut' : 'completed') : 'fallback');
		callback();
	};
	const timer = window.setTimeout(() => {
		if (cancelled) return;
		// The idle timeout starts at registration, after the initial yield to a fresh task.
		finishWait = measureWait();
		if (
			typeof window.requestIdleCallback === 'function' &&
			typeof window.cancelIdleCallback === 'function'
		) {
			// eslint-disable-next-line compat/compat
			idleHandle = window.requestIdleCallback(run, { timeout });
		} else {
			run();
		}
	}, 0);
	return () => {
		cancelled = true;
		finishWait?.('cancelled');
		window.clearTimeout(timer);
		if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
	};
}

// `overflow-x: hidden` alone computes `overflow-y` to `auto`, so check that the container scrolls.
function findScrollContainer(element: Element): Element | null {
	for (let parent = element.parentElement; parent; parent = parent.parentElement) {
		const { overflowY } = getComputedStyle(parent);
		if (
			(overflowY === 'auto' || overflowY === 'scroll') &&
			parent.scrollHeight > parent.clientHeight
		) {
			return parent;
		}
	}
	return null;
}
